import React, { useRef, useState } from 'react';
import { Upload, Sparkles, X, Loader2, Wand2, ImageIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import api from '../lib/api';
import { toast } from 'sonner';

// Compress + base64-encode the uploaded image to ~256x256 to keep payload small.
async function fileToDataUrl(file, maxDim = 256, quality = 0.85) {
  if (!file.type.startsWith('image/')) throw new Error('Please choose an image file');
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = () => rej(new Error('Could not read image'));
    i.src = URL.createObjectURL(file);
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', quality);
}

export default function AvatarPicker({ open, onOpenChange, currentAvatar, onSave }) {
  const [picked, setPicked] = useState(currentAvatar || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiSourceData, setAiSourceData] = useState(null); // optional reference selfie data URL
  const [aiResultUrl, setAiResultUrl] = useState(null);
  const fileRef = useRef(null);
  const aiRefFileRef = useRef(null);

  const handleAiRefFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file, 384);
      setAiSourceData(dataUrl);
      toast.success('Reference photo ready — add a prompt and click Generate');
    } catch (err) {
      toast.error(err.message || 'Could not load image');
    } finally {
      if (aiRefFileRef.current) aiRefFileRef.current.value = '';
    }
  };

  const handleAiGenerate = async () => {
    const prompt = aiPrompt.trim();
    if (prompt.length < 3) { toast.error('Please describe your avatar (at least 3 characters)'); return; }
    setAiGenerating(true);
    try {
      const { data } = await api.post('/avatars/generate', {
        prompt,
        source_image_b64: aiSourceData || undefined,
      });
      setAiResultUrl(data.data_url);
      setPicked(data.data_url);
      toast.success('Cartoon avatar generated — click Save to apply');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate avatar');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      setPicked(dataUrl);
      toast.success('Photo ready — click Save to apply');
    } catch (err) {
      toast.error(err.message || 'Could not load image');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!picked || picked === currentAvatar) { onOpenChange(false); return; }
    setSaving(true);
    // Track AI-generated status so Play compliance labels light up downstream
    const isAI = !!(aiResultUrl && picked === aiResultUrl);
    const ok = await onSave(picked, { isAiGenerated: isAI });
    setSaving(false);
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#161622] border-white/10 text-white max-w-xl" data-testid="avatar-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles size={16} className="text-fuchsia-400" />
            Choose your avatar
          </DialogTitle>
        </DialogHeader>

        {/* Preview */}
        <div className="flex items-center gap-4 p-3 rounded-2xl bg-white/5 border border-white/10">
          <div className="w-20 h-20 rounded-full overflow-hidden ring-2 ring-fuchsia-500/50 bg-[#0A0A0F] flex items-center justify-center">
            {picked ? (
              <img src={picked} alt="preview" className="w-full h-full object-cover" />
            ) : (
              <span className="text-white/40 text-xs">No preview</span>
            )}
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold">Live preview</div>
            <div className="text-xs text-white/50 mt-1">Generate an AI portrait or upload your own photo.</div>
          </div>
        </div>

        <Tabs defaultValue="ai" className="mt-3">
          <TabsList className="bg-[#0F0F18] border border-white/10">
            <TabsTrigger value="ai" className="data-[state=active]:bg-white/10" data-testid="tab-ai-generator">
              <Wand2 size={12} className="mr-1" /> AI generator
            </TabsTrigger>
            <TabsTrigger value="upload" className="data-[state=active]:bg-white/10" data-testid="tab-upload-photo">
              Upload photo
            </TabsTrigger>
          </TabsList>

          <TabsContent value="ai" className="mt-3">
            <div className="space-y-3">
              <div className="rounded-2xl p-4 bg-gradient-to-br from-fuchsia-500/10 via-purple-500/10 to-cyan-500/10 border border-fuchsia-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Wand2 size={14} className="text-fuchsia-300" />
                  <span className="text-sm font-display font-semibold">Generate a cartoon avatar with AI</span>
                </div>
                <p className="text-xs text-white/50 mb-2">
                  Describe yourself or your perfect cartoon character. Optionally upload a selfie and AI will turn it into a chibi cartoon while keeping your features recognizable.
                </p>
                <Textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder='e.g. "Young woman with purple hair and a cosmic crown, smiling"'
                  rows={3}
                  className="bg-[#0F0F18] border-white/10 text-sm resize-none"
                  data-testid="ai-prompt-input"
                />

                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <input
                    ref={aiRefFileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAiRefFile}
                    data-testid="ai-ref-input"
                  />
                  {aiSourceData ? (
                    <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-cyan-500/10 border border-cyan-400/30 text-xs">
                      <img src={aiSourceData} alt="ref" className="w-5 h-5 rounded-full object-cover" />
                      <span className="text-cyan-200">Reference photo attached</span>
                      <button onClick={() => setAiSourceData(null)} className="text-white/40 hover:text-rose-300">
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => aiRefFileRef.current?.click()}
                      className="text-xs text-cyan-300 hover:bg-cyan-500/10"
                      data-testid="ai-attach-ref-btn"
                    >
                      <ImageIcon size={12} className="mr-1" /> Attach selfie (optional)
                    </Button>
                  )}
                  <div className="flex-1" />
                  <Button
                    onClick={handleAiGenerate}
                    disabled={aiGenerating || aiPrompt.trim().length < 3}
                    data-testid="ai-generate-btn"
                    className="bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0 rounded-full"
                  >
                    {aiGenerating
                      ? <><Loader2 size={14} className="mr-2 animate-spin" />Generating…</>
                      : <><Wand2 size={14} className="mr-2" />Generate</>}
                  </Button>
                </div>
              </div>

              {aiResultUrl && (
                <div className="rounded-2xl p-3 bg-[#0F0F18] border border-emerald-500/30">
                  <div className="text-xs text-emerald-300 mb-2 flex items-center gap-1">
                    <Sparkles size={12} /> Your AI cartoon avatar
                  </div>
                  <div className="relative w-40 h-40 mx-auto">
                    <img src={aiResultUrl} alt="AI avatar" className="w-40 h-40 rounded-2xl object-cover" data-testid="ai-result-image" />
                    <span
                      className="absolute bottom-1.5 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-black/70 text-fuchsia-200 border border-fuchsia-400/40 backdrop-blur"
                      data-testid="ai-generated-label-preview"
                      title="This image was generated by AI"
                    >
                      AI generated
                    </span>
                  </div>
                  <p className="mt-2 text-center text-[10px] text-white/45">
                    We&apos;ll tag this photo as <span className="text-fuchsia-300 font-semibold">AI generated</span> everywhere it&apos;s shown, per Google Play&apos;s Generative-AI transparency policy.
                  </p>
                </div>
              )}
              <div className="text-[10px] text-white/40">Powered by Gemini Nano Banana · 30 generations / day</div>
            </div>
          </TabsContent>

          <TabsContent value="upload" className="mt-3">
            <div className="rounded-2xl p-6 border border-dashed border-white/15 bg-white/3 text-center">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFile}
                data-testid="avatar-upload-input"
              />
              <Upload className="mx-auto text-cyan-400" size={28} />
              <div className="mt-3 text-sm">Upload a profile photo</div>
              <div className="text-xs text-white/50 mt-1">PNG, JPG or WebP — auto-resized to 256×256.</div>
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="mt-4 bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0 rounded-full"
                data-testid="avatar-upload-trigger"
              >
                {uploading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Upload size={14} className="mr-2" />}
                {uploading ? 'Processing…' : 'Choose photo'}
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-white/70 hover:text-white hover:bg-white/5"
            data-testid="avatar-cancel-btn"
          >
            <X size={14} className="mr-1" /> Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !picked || picked === currentAvatar}
            className="bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0"
            data-testid="avatar-save-btn"
          >
            {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />}
            Save avatar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
